import React from 'react';
import styled from 'styled-components';
import Layout from '@components/Layout';
import Meta from '@components/Meta';
import {Hero, HeroCaptionContainer} from '@components/Hero';
import {Headline} from '@components';
import SubHeadline from '@components/Headline/SubHeadline';
import {ThemeProvider} from '@styles/themes';
import {graphql} from 'gatsby';
import PairBox from '@components/PairBox';
import {PagePadding} from '@components/SectionLayout';
import {Icon, SolidButton} from '@components';
import IconCard from '@components/IconCard';
import InfoBox from '@components/InfoBox';
import * as colors from '@styles/colors';
import * as fonts from '@styles/fonts';
import {media} from '@styles';
import usda from '@images/icons/usda.png';
import gmo from '@images/icons/gmo.png';
import nohormones from '@images/icons/nohormones.png';
import get from 'lodash.get';

interface QueryDataProps {
  cms: {
    page: {
      seoTitle: string;
      searchDescription: string;
      content: any[]
    }
  }
}

const HeadlineContainer = styled.div`
  max-width: 350px;
  margin-bottom: 10px;
  margin: 0 auto;
`;

const SubHeadlineContainer = styled.div`
  max-width: 240px;
  margin: 0 auto 45px;

  ${media.tablet`
    margin: 0 auto 10px;
  `};
`;

const PairBoxOverRide = styled(PairBox)`
  // display: none;

  .pairBoxImage img {
    max-width: 800px;
    width: 100%;
  }

  ${media.tablet`
    .pairBoxImage {
      order: 2;
    }
    .pairBoxInfo {
      order: 1;
    }
  `}
`;

const InfoSection = styled.section`
  padding: 140px 0;

  ${media.tablet`
    padding: 40px 0;
  `}
`;

const IconCardListContainer = styled.div`
  display: flex;
  justify-content: center;

  ${media.tablet`
    flex-direction: column;
  `}
`;

const IconCardContainer = styled.div`
  margin: 0 1vw;
`;

const links = [
  {
    title: 'Contact Us',
    ariaLabel: 'Contact Us',
    url: '/contact-us',
  },
  {
    title: 'Export Experience',
    ariaLabel: 'Export Experience',
    url: '/export-assistance',
  },
];

const StatsList = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  list-style-type: none;

  li {
    color: ${colors.white};
    text-transform: uppercase;
    border-right: solid 2px ${colors.white};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 20px;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: 0;
      padding-right: 0;
    }
  }

  h6 {
    font-size: 1.5rem;
    font-family: ${fonts.franklinGothic};
    font-weight: bold;

    ${media.tablet`
      font-size: 1rem;
    `};
  }
`;

const StatsListHeader = styled.h2`
  margin-bottom: 20px; // move to container
  color: ${colors.white};
  font-size: 3.4375rem;
  font-family: ${fonts.superclarendon};
  text-align: center;

  ${media.tablet`
    padding: 0 15px;
    font-size: 2.25rem;
`};
`;

const PairBoxParagraphContainer = styled.div`
  margin-bottom: 30px;
`;

const AboutPage = ({data} : {data: QueryDataProps}): JSX.Element => {

  const cmsPageData = data.cms.page;
  const heroData = cmsPageData.content[0];
  const {
    whoweserveBlock,
    aboutBlock,
    infoBlock,
    farmerownedBlock,
    crosslinkBlock,
  } = cmsPageData.content[1] || {};
  return (
    <ThemeProvider>
      <Layout>
        <Meta
          title={cmsPageData.seoTitle || ''}
          description={cmsPageData.searchDescription || ''}
          keywords={[`Ingredients`, `Organic Valley Ingredients`, `About`]}
        />
        {heroData ? (
          <Hero gradient image={heroData.bgimageBlock}>
            {/* Position to alignment */}
            <HeroCaptionContainer width={900} xPosition="left">
              <HeadlineContainer>
                <Headline align={'center'}>{heroData.headerBlock}</Headline>
              </HeadlineContainer>
              <SubHeadlineContainer>
                <SubHeadline>{heroData.subheaderBlock}</SubHeadline>
              </SubHeadlineContainer>
              {heroData.stateBlock ? (
                <StatsList>
                  {
                    heroData.stateBlock.map((statsItem: {headerBlock: string, subheaderBlock: string}, index: number) : JSX.Element => (
                      <li>
                        <StatsListHeader>{heroData.stateBlock[index].headerBlock}</StatsListHeader> <h6>{heroData.stateBlock[index].subheaderBlock}</h6>
                      </li>
                    ))
                  }
                </StatsList>
              ) : null}
            </HeroCaptionContainer>
          </Hero>
        ): null}
        
        {whoweserveBlock ? (
          <PairBox image={whoweserveBlock.imageBlock} title={whoweserveBlock.headerBlock}>
            {whoweserveBlock.listBlock ? (
              <ul>
                {whoweserveBlock.listBlock.map((item: string) => (<li>{item}</li>))}
              </ul>
            ) : null}
          </PairBox>
        ) : null}
        
        {aboutBlock ? (
          <PairBoxOverRide
            reverse
            image={aboutBlock.imageBlock}
            title={aboutBlock.headerBlock}
            background={`${colors.creme}`}
          >
            {aboutBlock.bodyBlock ? (
              <PairBoxParagraphContainer>
                <p>{aboutBlock.bodyBlock}</p>
              </PairBoxParagraphContainer>
            ) : null}
            {aboutBlock.pagelinkBlock ? (
              <SolidButton
                ariaLabel={aboutBlock.pagelinkBlock.title}
                url={aboutBlock.pagelinkBlock.url}
              >
                {aboutBlock.pagelinkBlock.url === '/products/'
                  || aboutBlock.pagelinkBlock.title === 'Products'
                  ? "View Our Products" : aboutBlock.pagelinkBlock.title}
              </SolidButton>
            ) : null}
          </PairBoxOverRide>
        ) : null}

        <InfoSection>
          <div
            css={`
            max-width: 1600px;
            margin: 0 auto;
          `}
          >
            <PagePadding>
              <h2
                css={`
                text-align: center;
                margin-bottom: 30px;
              `}
              >
                Organic Certification
            </h2>
              <p
                css={`
                text-align: center;
                margin-bottom: 60px;
              `}
              >
                USDA-certified organic products are approved with organic
                equivalency with many regions, including the EU, China, Australia,
                New Zealand and many others. As a point of comparison, EU organic
                certified products may not be exported to the U.S. unless
                additional affidavits are provided assuring that none of the cows
                have ever received any antibiotics. Our experts can also assist
                you with non-GMO project verification as well as certification for
                halal, kosher, 100% grass-fed and Chinese import requirements.
              </p>

              <IconCardListContainer>
                <IconCardContainer>
                  <IconCard
                    line
                    title="USDA ORGANIC"
                    caption="Our products are certified organic by the USDA."
                  >
                    <Icon src={usda} width={90} height={90} />
                  </IconCard>
                </IconCardContainer>
                <IconCardContainer>
                  <IconCard
                    line
                    title="NO GMOS"
                    caption="Our organic animal feed does not contain GMOs to ensure the highest quality."
                  >
                    <Icon src={gmo} width={113} height={90} />
                  </IconCard>
                </IconCardContainer>
                <IconCardContainer>
                  <IconCard
                    line
                    title="NO HORMONES OR ANTIBIOTICS"
                    caption="Our farmers do not use antibiotics, synthetic hormones or toxic pesticides."
                  >
                    <Icon src={nohormones} width={105} height={90} />
                  </IconCard>
                </IconCardContainer>
              </IconCardListContainer>
            </PagePadding>
          </div>
        </InfoSection>

        {infoBlock ? <InfoBox image={get(infoBlock, 'bgimageBlock.childImageSharp.fluid.base64', null)}>{infoBlock.bodyBlock}</InfoBox> : null}

        {farmerownedBlock ? (
          <PairBox
            reverse
            stale
            image={farmerownedBlock.imageBlock}
            title={farmerownedBlock.headerBlock}
          >
            {farmerownedBlock.bodyBlock ? (
              <PairBoxParagraphContainer>
                <p>{farmerownedBlock.bodyBlock}</p>
              </PairBoxParagraphContainer>
            ): null}
            {farmerownedBlock.pagelinkBlock ? (
              <SolidButton
                ariaLabel={farmerownedBlock.pagelinkBlock.title}
                url={farmerownedBlock.pagelinkBlock.url}
              >
                {farmerownedBlock.pagelinkBlock.url === '/products/'
                  || farmerownedBlock.pagelinkBlock.title === 'Products'
                  ? "View Our Products" : farmerownedBlock.pagelinkBlock.title}
              </SolidButton>
            ) : null}
          </PairBox>
        ) : null}

        {crosslinkBlock ? (
          <InfoBox
            buttonWidth={200}
            links={[
              crosslinkBlock.pagelink1Block,
              crosslinkBlock.pagelink2Block,
            ].filter(item => item !== null)}
            maxWidth="100%"
            color={colors.brown}
          >
            {crosslinkBlock.headerBlock}
          </InfoBox>
        ) : null}
      </Layout>
    </ThemeProvider>   
  );
};

export default AboutPage;

export const query = graphql`
  query AboutPageQuery {
    cms {
      page(urlPath: "/home/about/") {
        ... on CMS_AboutPage {
          seoTitle
          searchDescription
          content {
            ... on CMS_AboutHeroBlock {
              headerBlock
              subheaderBlock
              bgimageBlock {
                url
                childImageSharp {
                  fluid(maxWidth: 1440, quality: 100) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
              stateBlock {
                headerBlock
                subheaderBlock
              }
            }
            ... on CMS_AboutBodyBlock {
              whoweserveBlock {
                headerBlock
                listBlock
                imageBlock {
                  url
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
              aboutBlock {
                imageBlock {
                  url
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
                headerBlock
                bodyBlock
                pagelinkBlock {
                  url
                  title
                }
              }
              infoBlock {
                bgimageBlock {
                  url
                  childImageSharp {
                    fluid(maxWidth: 1440, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
                bodyBlock
              }
              farmerownedBlock {
                imageBlock {
                  url
                  childImageSharp {
                    fluid(maxWidth: 1440, quality: 100) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
                headerBlock
                bodyBlock
                pagelinkBlock {
                  url
                  title
                }
              }
              crosslinkBlock {
                headerBlock
                pagelink1Block {
                  url
                  title
                }
                pagelink2Block {
                  url
                  title
                }
              }
            }
          }
        }
      } 
    }
  }
`;
